/**
 * Filtering, statistics and reporting over a list of work orders.
 *
 * Pure: the work orders page, its statistics panel, the printable report and
 * the CSV export all read the same functions, so a figure on the screen and
 * the same figure in a report cannot disagree.
 *
 * Two rules carried from the rest of the application:
 *
 *  - **A repair type is never guessed.** It is the asset type the order was
 *    raised against, else the defect type it closes. An order raised by hand
 *    against neither is "Not classified", counted as such, not read from its
 *    title.
 *  - **Not recorded is not zero.** Mean time to complete, on-time rate, cost
 *    and labour each say how many orders they were computed from; with none,
 *    the figure is null and the screen says so.
 */
import {
  closedAt,
  isOpen,
  isOverdue,
  KIND_LABEL,
  STATUS_LABEL,
  WORK_ORDER_KINDS,
  WORK_ORDER_PRIORITIES,
  WORK_ORDER_STATUSES,
  type WorkOrder,
  type WorkOrderKind,
  type WorkOrderPriority,
  type WorkOrderStatus,
} from "./workOrders";
import { t } from "../i18n";
import {
  AREA_LABEL,
  AREAS,
  areaKey,
  areaOf,
  contractorKey,
  contractorOf,
  NOT_SET,
  OWN_TEAM,
  TRADE_LABEL,
  TRADES,
  tradeKey,
  tradeOf,
  type Area,
  type Trade,
} from "./workOrderClass";

export const UNCLASSIFIED = "Not classified";

/** The kind of repair: what it was raised against, never inferred from the title. */
export function repairTypeOf(wo: WorkOrder): string {
  return wo.assetTypeName ?? wo.defectTypeName ?? UNCLASSIFIED;
}

/** "passenger_processing" → "Passenger processing". */
export function disciplineLabel(key: string | undefined): string {
  if (!key) return UNCLASSIFIED;
  const words = key.replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/* ---- date range ---------------------------------------------------------- */

export const DATE_FIELDS = ["opened", "completed", "due"] as const;
export type DateField = (typeof DATE_FIELDS)[number];

export const DATE_FIELD_LABEL: Record<DateField, string> = {
  opened: "Opened",
  completed: "Completed",
  due: "Due",
};

export function dateOf(wo: WorkOrder, field: DateField): string | undefined {
  if (field === "opened") return wo.openedAt;
  if (field === "completed") return closedAt(wo);
  return wo.dueAt;
}

/** Local calendar day as YYYY-MM-DD, which is what a date input holds. */
export function isoDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Whether the order's date falls within [from, to], both whole local days and
 * both optional. With a range set, an order with no such date is outside it:
 * an open order has not been completed in any range.
 */
export function inDateRange(wo: WorkOrder, field: DateField, from?: string, to?: string): boolean {
  if (!from && !to) return true;
  const iso = dateOf(wo, field);
  if (!iso) return false;
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return false;
  if (from && ms < new Date(`${from}T00:00:00`).getTime()) return false;
  if (to && ms > new Date(`${to}T23:59:59.999`).getTime()) return false;
  return true;
}

export const RANGE_PRESETS = ["7d", "30d", "90d", "month", "year"] as const;
export type RangePreset = (typeof RANGE_PRESETS)[number];

export const RANGE_PRESET_LABEL: Record<RangePreset, string> = {
  "7d": "7 days",
  "30d": "30 days",
  "90d": "90 days",
  month: "This month",
  year: "Year to date",
};

export function presetRange(preset: RangePreset, now: Date): { from: string; to: string } {
  const to = isoDay(now);
  if (preset === "month") return { from: isoDay(new Date(now.getFullYear(), now.getMonth(), 1)), to };
  if (preset === "year") return { from: isoDay(new Date(now.getFullYear(), 0, 1)), to };
  const days = { "7d": 7, "30d": 30, "90d": 90 }[preset];
  const start = new Date(now);
  start.setDate(start.getDate() - (days - 1));
  return { from: isoDay(start), to };
}

/* ---- SLA ------------------------------------------------------------------ */

/**
 * Where an order stands against its SLA — the hours from opening the platform
 * holds as `sla_hours`.
 *
 *   met       completed inside the window        missed    completed after it
 *   breached  still open, past the window        at_risk   open, under a quarter left
 *   on_track  open, more than a quarter left     none      no SLA recorded
 *   void      cancelled — neither met nor missed
 *
 * With no SLA the answer is "none", never "met": an order nobody set a window
 * for has not kept one.
 */
export const SLA_STATES = ["breached", "at_risk", "on_track", "met", "missed", "none", "void"] as const;
export type SlaState = (typeof SLA_STATES)[number];

export const SLA_LABEL: Record<SlaState, string> = {
  breached: "Breached",
  at_risk: "At risk",
  on_track: "On track",
  met: "Met",
  missed: "Missed",
  none: "No SLA",
  void: "Canceled",
};

/** The share of the window left below which an open order is at risk. */
export const SLA_AT_RISK_FRACTION = 0.25;

/**
 * The deadline, with time held for parts added back on.
 *
 * `heldMs` is time this job spent on hold WAITING FOR A PART, and nothing
 * else — see `domain/partsHold.ts`, which computes it from the job's own
 * transition history. It is optional, and omitting it gives exactly the
 * behaviour this function always had, so every caller that does not know a
 * job's history is unchanged.
 *
 * Why it exists: a bearing on a ship for three weeks is not a maintenance
 * team being slow, and a service level that cannot tell those apart is one
 * every supervisor learns to explain away. Only the parts wait is excluded.
 * A job held because nobody turned up keeps its clock running, which is what
 * makes the exclusion defensible rather than an excuse.
 */
export function slaDeadline(wo: WorkOrder, heldMs = 0): Date | null {
  if (wo.slaHours == null) return null;
  const opened = time(wo.openedAt);
  return Number.isNaN(opened) ? null : new Date(opened + wo.slaHours * 3_600_000 + Math.max(0, heldMs));
}

export function slaState(wo: WorkOrder, now: Date, heldMs = 0): SlaState {
  const deadline = slaDeadline(wo, heldMs);
  if (!deadline || wo.slaHours == null) return "none";
  if (wo.status === "cancelled") return "void";
  if (!isOpen(wo.status)) {
    const done = time(wo.completedAt);
    if (Number.isNaN(done)) return "none";
    return done <= deadline.getTime() ? "met" : "missed";
  }
  const left = deadline.getTime() - now.getTime();
  if (left < 0) return "breached";
  return left < wo.slaHours * 3_600_000 * SLA_AT_RISK_FRACTION ? "at_risk" : "on_track";
}

/** "3 h left", "breached by 2 d", "met with 5 h to spare" — for a table cell. */
export function slaRemaining(wo: WorkOrder, now: Date, heldMs = 0): string | null {
  const deadline = slaDeadline(wo, heldMs);
  if (!deadline) return null;
  const state = slaState(wo, now, heldMs);
  const span = (ms: number) => {
    const h = Math.abs(ms) / 3_600_000;
    // One decimal of a day, not a rounded day: "3 d left" for sixty hours promises twelve that are not there.
    return h >= 48 ? `${(h / 24).toFixed(1).replace(/\.0$/, "")} d` : `${Math.max(1, Math.round(h))} h`;
  };
  if (state === "breached") return t("breached by {span}", { span: span(now.getTime() - deadline.getTime()) });
  if (state === "at_risk" || state === "on_track")
    return t("{span} left", { span: span(deadline.getTime() - now.getTime()) });
  if (state === "met") return t("{span} to spare", { span: span(deadline.getTime() - time(wo.completedAt)) });
  if (state === "missed") return t("late by {span}", { span: span(time(wo.completedAt) - deadline.getTime()) });
  return null;
}

/* ---- filters and sort ---------------------------------------------------- */

export type Scope = "open" | "all" | "closed";

export const SORTS = [
  "priority",
  "opened_desc",
  "opened_asc",
  "due_asc",
  "sla_deadline",
  "completed_desc",
  "age_desc",
] as const;
export type SortKey = (typeof SORTS)[number];

export const SORT_LABEL: Record<SortKey, string> = {
  priority: "Priority, then oldest",
  opened_desc: "Newest opened",
  opened_asc: "Oldest opened",
  due_asc: "Due soonest",
  sla_deadline: "SLA deadline soonest",
  completed_desc: "Recently completed",
  age_desc: "Longest running",
};

export interface WorkOrderFilters {
  scope: Scope;
  status: WorkOrderStatus | null;
  priority: WorkOrderPriority | null;
  kind: WorkOrderKind | null;
  repairType: string | null;
  discipline: string | null;
  /** Area, trade and contractor keys (workOrderClass.ts); "none" / "Own team" for not set. */
  area?: string | null;
  trade?: string | null;
  contractor?: string | null;
  sla: SlaState | null;
  overdueOnly: boolean;
  q: string;
  dateField: DateField;
  from?: string;
  to?: string;
  sort: SortKey;
}

export function matches(wo: WorkOrder, f: WorkOrderFilters, now: Date): boolean {
  if (f.scope === "open" && !isOpen(wo.status)) return false;
  if (f.scope === "closed" && isOpen(wo.status)) return false;
  if (f.status && wo.status !== f.status) return false;
  if (f.priority && wo.priority !== f.priority) return false;
  if (f.kind && wo.kind !== f.kind) return false;
  if (f.repairType && repairTypeOf(wo) !== f.repairType) return false;
  if (f.discipline && disciplineLabel(wo.discipline) !== f.discipline) return false;
  if (f.area && areaKey(wo) !== f.area) return false;
  if (f.trade && tradeKey(wo) !== f.trade) return false;
  if (f.contractor && contractorKey(wo) !== f.contractor) return false;
  if (f.sla && slaState(wo, now) !== f.sla) return false;
  if (f.overdueOnly && !isOverdue(wo, now)) return false;
  if (!inDateRange(wo, f.dateField, f.from, f.to)) return false;
  const needle = f.q.trim().toLowerCase();
  if (
    needle &&
    ![
      wo.number,
      wo.title,
      wo.assetTag,
      wo.zoneCode,
      wo.crew,
      wo.description,
      repairTypeOf(wo),
      disciplineLabel(wo.discipline),
      contractorOf(wo) ?? undefined,
      tradeOf(wo).value ? TRADE_LABEL[tradeOf(wo).value!] : undefined,
    ].some((x) => x?.toLowerCase().includes(needle))
  )
    return false;
  return true;
}

const time = (iso: string | undefined) => (iso ? new Date(iso).getTime() : NaN);

/** Orders with no value for the sort's date go last, whichever direction. */
function byDate(a: string | undefined, b: string | undefined, dir: 1 | -1): number {
  const ta = time(a);
  const tb = time(b);
  if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
  if (Number.isNaN(ta)) return 1;
  if (Number.isNaN(tb)) return -1;
  return (ta - tb) * dir;
}

function ageMs(wo: WorkOrder, now: Date): number {
  return (closedAt(wo) ? time(closedAt(wo)) : now.getTime()) - time(wo.openedAt);
}

export function sortOrders(list: WorkOrder[], sort: SortKey, now: Date): WorkOrder[] {
  const out = [...list];
  const cmp: Record<SortKey, (a: WorkOrder, b: WorkOrder) => number> = {
    priority: (a, b) =>
      Number(isOpen(b.status)) - Number(isOpen(a.status)) || a.priority - b.priority || ageMs(b, now) - ageMs(a, now),
    opened_desc: (a, b) => byDate(a.openedAt, b.openedAt, -1),
    opened_asc: (a, b) => byDate(a.openedAt, b.openedAt, 1),
    due_asc: (a, b) => byDate(a.dueAt, b.dueAt, 1),
    sla_deadline: (a, b) => byDate(slaDeadline(a)?.toISOString(), slaDeadline(b)?.toISOString(), 1),
    completed_desc: (a, b) => byDate(closedAt(a), closedAt(b), -1),
    age_desc: (a, b) => ageMs(b, now) - ageMs(a, now),
  };
  return out.sort((a, b) => cmp[sort](a, b) || a.number.localeCompare(b.number));
}

/* ---- statistics ---------------------------------------------------------- */

export interface Tally {
  key: string;
  label: string;
  count: number;
}

export interface WorkOrderStats {
  total: number;
  open: number;
  closed: number;
  cancelled: number;
  overdue: number;
  p1Open: number;
  byStatus: Tally[];
  byPriority: Tally[];
  byKind: Tally[];
  byRepairType: Tally[];
  byDiscipline: Tally[];
  byArea: Tally[];
  byTrade: Tally[];
  byContractor: Tally[];
  bySla: Tally[];
  /** Orders with an SLA that were not cancelled. */
  slaTracked: number;
  slaMet: number;
  slaMissed: number;
  slaBreached: number;
  slaAtRisk: number;
  /** Met over met + missed: completed orders only, since an open one has not finished. */
  slaCompliancePct: number | null;
  /** Per priority, the same compliance — where a contract's service levels are usually set. */
  slaByPriority: {
    priority: WorkOrderPriority;
    tracked: number;
    met: number;
    missed: number;
    breached: number;
    compliancePct: number | null;
  }[];
  /** Opened → completed, over orders that record both. */
  meanDaysToComplete: number | null;
  completedWithDates: number;
  /** Completed on or before due, over completed orders that had a due date. */
  onTimePct: number | null;
  completedWithDue: number;
  costActual: number;
  withCost: number;
  labourHours: number;
  withLabour: number;
}

function tally<T extends string | number>(
  orders: readonly WorkOrder[],
  pick: (w: WorkOrder) => T,
  order?: readonly T[],
  label: (k: T) => string = String,
): Tally[] {
  const counts = new Map<T, number>();
  for (const k of order ?? []) counts.set(k, 0);
  for (const w of orders) counts.set(pick(w), (counts.get(pick(w)) ?? 0) + 1);
  const rows = [...counts].map(([k, count]) => ({ key: String(k), label: label(k), count }));
  // A fixed order (status, priority, kind) keeps its order; an open set is by size.
  return order ? rows : rows.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export function workOrderStats(orders: readonly WorkOrder[], now: Date): WorkOrderStats {
  let open = 0;
  let closed = 0;
  let cancelled = 0;
  let overdue = 0;
  let p1Open = 0;
  let daysSum = 0;
  let completedWithDates = 0;
  let onTime = 0;
  let completedWithDue = 0;
  let costActual = 0;
  let withCost = 0;
  let labourHours = 0;
  let withLabour = 0;
  const slaCounts: Record<SlaState, number> = {
    breached: 0,
    at_risk: 0,
    on_track: 0,
    met: 0,
    missed: 0,
    none: 0,
    void: 0,
  };
  const perPriority = new Map<WorkOrderPriority, { tracked: number; met: number; missed: number; breached: number }>(
    WORK_ORDER_PRIORITIES.map((p) => [p, { tracked: 0, met: 0, missed: 0, breached: 0 }]),
  );

  for (const w of orders) {
    if (isOpen(w.status)) {
      open += 1;
      if (w.priority === 1) p1Open += 1;
      if (isOverdue(w, now)) overdue += 1;
    } else if (w.status === "cancelled") {
      cancelled += 1;
    } else {
      closed += 1;
    }
    if (w.status !== "cancelled" && w.completedAt) {
      const days = (time(w.completedAt) - time(w.openedAt)) / 86_400_000;
      if (Number.isFinite(days)) {
        daysSum += Math.max(0, days);
        completedWithDates += 1;
      }
      if (w.dueAt) {
        completedWithDue += 1;
        if (time(w.completedAt) <= time(w.dueAt)) onTime += 1;
      }
    }
    if (w.costActual != null) {
      costActual += w.costActual;
      withCost += 1;
    }
    if (w.labourHours != null) {
      labourHours += w.labourHours;
      withLabour += 1;
    }
    const sla = slaState(w, now);
    slaCounts[sla] += 1;
    if (sla !== "none" && sla !== "void") {
      const row = perPriority.get(w.priority)!;
      row.tracked += 1;
      if (sla === "met") row.met += 1;
      if (sla === "missed") row.missed += 1;
      if (sla === "breached") row.breached += 1;
    }
  }
  const pct = (met: number, missed: number) => (met + missed ? (100 * met) / (met + missed) : null);

  return {
    total: orders.length,
    open,
    closed,
    cancelled,
    overdue,
    p1Open,
    byStatus: tally(
      orders,
      (w) => w.status,
      WORK_ORDER_STATUSES,
      (k) => t(STATUS_LABEL[k]),
    ).filter((r) => r.count > 0),
    byPriority: tally(
      orders,
      (w) => w.priority,
      WORK_ORDER_PRIORITIES,
      (k) => `P${k}`,
    ),
    byKind: tally(
      orders,
      (w) => w.kind,
      WORK_ORDER_KINDS,
      (k) => t(KIND_LABEL[k]),
    ).filter((r) => r.count > 0),
    byRepairType: tally(orders, repairTypeOf, undefined, unclassifiedLabel),
    byDiscipline: tally(orders, (w) => disciplineLabel(w.discipline), undefined, unclassifiedLabel),
    byArea: tally(orders, areaKey, [...AREAS, NOT_SET], areaLabel).filter((r) => r.count > 0),
    byTrade: tally(orders, tradeKey, [...TRADES, NOT_SET], tradeLabel).filter((r) => r.count > 0),
    byContractor: tally(orders, contractorKey, undefined, contractorLabel),
    bySla: SLA_STATES.map((k) => ({ key: k, label: t(SLA_LABEL[k]), count: slaCounts[k] })).filter((r) => r.count > 0),
    slaTracked: orders.length - slaCounts.none - slaCounts.void,
    slaMet: slaCounts.met,
    slaMissed: slaCounts.missed,
    slaBreached: slaCounts.breached,
    slaAtRisk: slaCounts.at_risk,
    slaCompliancePct: pct(slaCounts.met, slaCounts.missed),
    slaByPriority: [...perPriority].map(([priority, r]) => ({ priority, ...r, compliancePct: pct(r.met, r.missed) })),
    meanDaysToComplete: completedWithDates ? daysSum / completedWithDates : null,
    completedWithDates,
    onTimePct: completedWithDue ? (100 * onTime) / completedWithDue : null,
    completedWithDue,
    costActual,
    withCost,
    labourHours,
    withLabour,
  };
}

export function areaLabel(k: string): string {
  return k === NOT_SET ? t("Area not set") : t(AREA_LABEL[k as Area] ?? k);
}
export function tradeLabel(k: string): string {
  return k === NOT_SET ? t("Trade not set") : t(TRADE_LABEL[k as Trade] ?? k);
}
export function contractorLabel(k: string): string {
  return k === OWN_TEAM ? t("Own team") : k;
}

/** A tally label: the value itself, except "Not classified", which is this application's own words. */
function unclassifiedLabel(k: string): string {
  return k === UNCLASSIFIED ? t("Not classified") : k;
}

/* ---- export -------------------------------------------------------------- */

const CSV_COLUMNS: [string, (w: WorkOrder) => string | number | undefined][] = [
  ["Number", (w) => w.number],
  ["Title", (w) => w.title],
  ["Repair type", repairTypeOf],
  ["Discipline", (w) => disciplineLabel(w.discipline)],
  ["Area", (w) => (areaOf(w).value ? AREA_LABEL[areaOf(w).value!] : undefined)],
  ["Trade", (w) => (tradeOf(w).value ? TRADE_LABEL[tradeOf(w).value!] : undefined)],
  ["Contractor", (w) => contractorOf(w) ?? "Own team"],
  ["Kind", (w) => KIND_LABEL[w.kind]],
  ["Priority", (w) => `P${w.priority}`],
  ["Status", (w) => STATUS_LABEL[w.status]],
  ["Asset", (w) => w.assetTag],
  ["Zone", (w) => w.zoneCode],
  ["Crew", (w) => w.crew],
  ["Opened", (w) => w.openedAt],
  ["Due", (w) => w.dueAt],
  ["Completed", (w) => closedAt(w)],
  ["SLA hours", (w) => w.slaHours],
  ["SLA deadline", (w) => slaDeadline(w)?.toISOString()],
  ["SLA status", (w) => SLA_LABEL[slaState(w, new Date())]],
  ["Labour hours", (w) => w.labourHours],
  ["Actual cost", (w) => w.costActual],
];

function csvCell(v: string | number | undefined): string {
  if (v == null) return "";
  const s = String(v);
  // Quote anything with a separator, quote or newline; neutralise a leading
  // formula character so a title cannot run as a spreadsheet formula.
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function workOrdersCsv(orders: readonly WorkOrder[]): string {
  const head = CSV_COLUMNS.map(([h]) => h).join(",");
  const body = orders.map((w) => CSV_COLUMNS.map(([, get]) => csvCell(get(w))).join(","));
  return [head, ...body].join("\r\n") + "\r\n";
}

/** A one-line account of the filters, for a report's header and a file name. */
export function describeFilters(f: WorkOrderFilters): string[] {
  const parts: string[] = [];
  parts.push(f.scope === "open" ? t("Open orders") : f.scope === "closed" ? t("Closed orders") : t("All orders"));
  if (f.from || f.to) {
    const field = t(DATE_FIELD_LABEL[f.dateField]);
    parts.push(
      f.from && f.to
        ? t("{field} from {from} to {to}", { field, from: f.from, to: f.to })
        : f.from
          ? t("{field} from {from}", { field, from: f.from })
          : t("{field} to {to}", { field, to: f.to ?? "" }),
    );
  }
  if (f.status) parts.push(t("Status: {status}", { status: t(STATUS_LABEL[f.status]) }));
  if (f.priority) parts.push(t("Priority: {priority}", { priority: `P${f.priority}` }));
  if (f.kind) parts.push(t("Kind: {kind}", { kind: t(KIND_LABEL[f.kind]) }));
  if (f.repairType) parts.push(t("Repair type: {type}", { type: unclassifiedLabel(f.repairType) }));
  if (f.discipline) parts.push(t("Discipline: {discipline}", { discipline: unclassifiedLabel(f.discipline) }));
  if (f.area) parts.push(t("Area: {area}", { area: areaLabel(f.area) }));
  if (f.trade) parts.push(t("Trade: {trade}", { trade: tradeLabel(f.trade) }));
  if (f.contractor) parts.push(t("Contractor: {name}", { name: contractorLabel(f.contractor) }));
  if (f.sla) parts.push(t("SLA: {sla}", { sla: t(SLA_LABEL[f.sla]) }));
  if (f.overdueOnly) parts.push(t("Overdue only"));
  if (f.q.trim()) parts.push(t("Search: “{q}”", { q: f.q.trim() }));
  return parts;
}
